import './rule-editor-profiles.scss';
import { EditableRule } from 'src/model/EditableRule';
import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { ComponentNamesForPermission, FeatureName, getEnumOptions, ProfilesOptions } from 'src/utils/enums';
import { dnsProtectionFeature, 
    spywareProtectionFeature, 
    ThreatFeatureStructure, 
    virusProtectionFeature, 
    vulnerabilityProtectionFeature } from 'src/components/features/protection-plus/protection-plus-config';
import { Popover, Select } from 'antd';
import { ProtectPermissions } from 'src/components/shared/protect-permissions/protect-permissions';
import CustomIcon, { IconType } from 'src/components/shared/custom-icon/custom-icon';
import FormField from 'src/components/shared/edit-row-modal/form-field/form-field';
import Arrow from '@svg/chevron-down_20.svg?react';
import { isAllowedServiceRule, isAllowListRule } from 'src/utils/ruleUtils';
import { TooltipDisableReason } from 'src/components/shared/tooltip-disable-message/tooltip-disable-reason';
import { DNS_DISABLE_ANTISPYWARE_INACTIVE } from 'src/utils/constants';

interface RuleEditorProfilesProps {
    ruleData: EditableRule;
    handleChange: (name: any, value: any) => void;
    allowDns: boolean
}

const ProfilesOptionInfo: React.FC<{ title: string, explain: string }> = ({ title, explain }) => {
    return <div className="profiles-option-info">
        <div className="title-and-explain">
            <div className="title-option">{title}</div>
            <div className="explain">{explain}</div>
        </div>
    </div>;
};

const FeatureItem: React.FC<{
    feature: ThreatFeatureStructure;
    value: any;
    hasPermissions: boolean;
    isDisabled: boolean;
    disableReason?: string;
    onSelect: (value: any) => void;
    noteElement?: React.ReactNode;
}> = ({ feature, value, hasPermissions, isDisabled, disableReason, onSelect, noteElement }) => {
    

    const getOptions = (nameForUpdate?: FeatureName) => {
        if (FeatureName.DNS === nameForUpdate) return getEnumOptions(ProfilesOptions).filter(_ => _.label !== ProfilesOptions.INACTIVE);
        return getEnumOptions(ProfilesOptions);
    };

    return <div className="protection-plus-option" key={feature.nameForUpdate}>
        <FormField label={feature.key}>
            <TooltipDisableReason disableReason={disableReason} missingPermissions={!hasPermissions}>
                <Select key={feature.nameForUpdate}
                    className={`protection-option-select ${typeof value === 'string' ? value.toLowerCase() : ''}`}
                    disabled={isDisabled}
                    popupClassName="dropdown-custom" suffixIcon={<Arrow />}
                    value={(!hasPermissions || isDisabled) ? ProfilesOptions.INACTIVE : value}
                    placeholder={!hasPermissions ? 'Select' : ''}
                    getPopupContainer={triggerNode => triggerNode.parentElement}
                    options={getOptions(feature.nameForUpdate)}
                    onSelect={onSelect}
                />
            </TooltipDisableReason>
            <Popover content={<ProfilesOptionInfo title={String(feature.nameForUpdate)} explain={feature.explain} />}
                showArrow={false}
                placement={'right'}>
                <CustomIcon type={IconType.INFO} className={'info-icon'} />
            </Popover>
            {noteElement}
        </FormField>
    </div>;
};

export const RuleEditorProfiles: React.FC<RuleEditorProfilesProps> = observer(({
    ruleData,
    handleChange,
    allowDns
}) => {
    const isAllowListFromOrToInternet = useMemo(() => {
        if (!ruleData.id) {
            return false;
        }
        return isAllowListRule(ruleData.name);
    }, [ruleData.id]);

    const isServiceRule = () => {
        return isAllowedServiceRule(ruleData.name);
    };

    const noteElementForDNS = (threatItem: ThreatFeatureStructure) => {
        const arrowIcon = <CustomIcon type={IconType.ARROW_RIGHT} />;
        return FeatureName.DNS === threatItem.nameForUpdate && ruleData[threatItem.keyForGetValue as keyof EditableRule] === ProfilesOptions.BLOCK
            && <div className="note-message"><span>Note:</span> You can manage DNS exceptions under Policy {arrowIcon} Web Filtering {arrowIcon} Domains/DNS</div>;
    };

    const noteElementForWebFiltering = () => {
        const arrowIcon = <CustomIcon type={IconType.ARROW_RIGHT} />;
        return ruleData.urlFiltering === 'Active' &&
            <div className="note-message indent-as-info-popover"><span>Note:</span> You can manage Web Protection under Policy {arrowIcon} Web Filtering</div>;
    };

    const isDnsDisable = (nameForUpdate: FeatureName | undefined) => {
        return FeatureName.DNS === nameForUpdate && ruleData['antiSpyware'] === ProfilesOptions.INACTIVE;
    };

    const onSelect = (value: any, keyForGetValue?: string) => {
        if (keyForGetValue === 'antiSpyware') {
            if (value === ProfilesOptions.INACTIVE) {
                handleChange('dns', ProfilesOptions.INACTIVE);
            }
            else if (ruleData[keyForGetValue] === ProfilesOptions.INACTIVE) {
                handleChange('dns', ProfilesOptions.ALERT);
            }
        }
        handleChange(keyForGetValue, value);
    };

    const getDisableReason = (nameForUpdate: FeatureName | undefined) => {
        if (isDnsDisable(nameForUpdate)) return DNS_DISABLE_ANTISPYWARE_INACTIVE;
    };

    const advancedFeatures = [...(allowDns ?[] : [dnsProtectionFeature]), virusProtectionFeature, spywareProtectionFeature, vulnerabilityProtectionFeature];

    return <div className={'rule-editor-profiles'}>
        <FormField label={'Default Web Protection'}>
            <Select className={`protection-option-select ${ruleData.urlFiltering.toLowerCase()}`}
                    popupClassName="dropdown-custom"
                    value={ruleData.urlFiltering}
                    placeholder={'Select'} suffixIcon={<Arrow />}
                    disabled={ruleData.isDefault && !isServiceRule()}
                    getPopupContainer={(triggerNode) => triggerNode.parentElement}
                    options={[{value: 'Active', label: 'Active'}, {value: 'Inactive', label: 'Inactive'}]}
                    onSelect={(value) => handleChange('urlFiltering', value)}
            />
            {noteElementForWebFiltering()}
        </FormField>
        {allowDns && (
            <FeatureItem
                feature={dnsProtectionFeature}
                value={ruleData[dnsProtectionFeature.keyForGetValue as keyof EditableRule]}
                hasPermissions={true}
                isDisabled={ruleData.action === 'drop' || isAllowListFromOrToInternet || isDnsDisable(dnsProtectionFeature.nameForUpdate)}
                disableReason={getDisableReason(dnsProtectionFeature.nameForUpdate)}
                onSelect={value => onSelect(value, dnsProtectionFeature.keyForGetValue)}
                noteElement={noteElementForDNS(dnsProtectionFeature)}
            />
        )}
        <ProtectPermissions componentName={ComponentNamesForPermission.AdvancedThreatProtection}>
            {(hasPermissions: boolean, UpgradeButton?: () => JSX.Element) => <>
                <div className="title-group-with-upgrade-button">
                    <div className="title-group">Premium Threat Protection</div>
                    {UpgradeButton ? <UpgradeButton /> : null}
                </div>
                {advancedFeatures.map((feature) => (
                    <FeatureItem
                        key={feature.nameForUpdate}
                        feature={feature}
                        value={ruleData[feature.keyForGetValue as keyof EditableRule]}
                        hasPermissions={hasPermissions}
                        isDisabled={ruleData.action === 'drop' || !hasPermissions || isAllowListFromOrToInternet || isDnsDisable(feature.nameForUpdate)}
                        disableReason={getDisableReason(feature.nameForUpdate)}
                        onSelect={value => onSelect(value, feature.keyForGetValue)}
                        noteElement={noteElementForDNS(feature)}
                    />
                ))}
            </>}
        </ProtectPermissions>
    </div>;
});